import React, { useState, useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, PanResponder } from 'react-native';
import type { LayoutChangeEvent } from 'react-native';
import Svg, { Path, G, Circle, Line, Rect, Text as SvgText } from 'react-native-svg';
import type { TrailPoint } from '@/types/trail';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { useTranslation } from 'react-i18next';
import { calculateDistance, calculateTotalDistance, formatPace } from '@/utils/distance';

const ACTIVITY_COLORS: Record<string, string> = {
  still: '#A0A883',
  on_foot: '#D4FF5E',
  walking: '#D4FF5E',
  running: '#FFFE77',
  on_bicycle: '#40C4FF',
  in_vehicle: '#FF8A65',
  unknown: '#BEC056',
};

interface SparklineProps {
  data: number[];
  /** Fixed width; omit to track the parent width via onLayout. */
  width?: number;
  height?: number;
  color?: string;
  fillColor?: string;
  strokeWidth?: number;
  unit?: string;
  formatValue?: (v: number) => string;
  maxPoints?: number;
  /** Render a y-axis: spine + dashed gridlines + min/mid/max labels. */
  showYAxis?: boolean;
  /** Render an x-axis: bottom spine + tick marks + labels at 0/25/50/75/100%. */
  showXAxis?: boolean;
  /** Label for a fraction (0-1) of the x span, e.g. distance covered. */
  xLabel?: (fraction: number) => string;
}

interface PointMetrics {
  min: number;
  max: number;
  range: number;
  stepX: number;
  /** vertical (top/bottom) padding */
  padding: number;
  /** horizontal (left/right) padding */
  padL: number;
  padR: number;
}

function downsample<T>(items: T[], max = 250): T[] {
  if (items.length <= max) return items;
  const step = items.length / max;
  const out: T[] = [];
  for (let i = 0; i < max; i++) {
    out.push(items[Math.floor(i * step)]);
  }
  return out;
}

function useMetrics(data: number[], width: number, _height: number, padL = 6, padR = 6): PointMetrics {
  return useMemo(() => {
    const padding = 6; // vertical (top/bottom)
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = data.length > 1 ? (width - padL - padR) / (data.length - 1) : 0;
    return { min, max, range, stepX, padding, padL, padR };
  }, [data, width, padL, padR]);
}

function smoothPath(data: number[], width: number, height: number, padL: number, padR: number, padTB: number): string {
  if (data.length < 2) return '';
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = (width - padL - padR) / (data.length - 1);

  const yFor = (v: number) => height - padTB - ((v - min) / range) * (height - padTB * 2);
  const xFor = (i: number) => padL + i * stepX;

  const pts = data.map((v, i) => ({ x: xFor(i), y: yFor(v) }));

  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const cp1x = p0.x + (p1.x - p0.x) / 2;
    const cp1y = p0.y;
    const cp2x = p0.x + (p1.x - p0.x) / 2;
    const cp2y = p1.y;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p1.x} ${p1.y}`;
  }
  return d;
}

function Sparkline({
  data,
  width,
  height = 96,
  color = theme.accentPrimary,
  fillColor = 'rgba(255,254,119,0.12)',
  strokeWidth = 2.5,
  unit = '',
  formatValue = (v: number) => `${Math.round(v * 10) / 10}`,
  maxPoints = 250,
  showYAxis = false,
  showXAxis = false,
  xLabel,
}: SparklineProps) {
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  const handleLayout = useCallback(
    (e: LayoutChangeEvent) => setMeasuredWidth(e.nativeEvent.layout.width),
    [],
  );
  const w = width ?? measuredWidth ?? 0;
  const X_AXIS_STRIP = showXAxis && xLabel ? 16 : 0;
  // labels live left of the plot area when the y-axis is on
  const Y_AXIS_LABEL_W = 38;
  const padL = showYAxis ? Y_AXIS_LABEL_W + 8 : 6;
  const padR = 6;
  const chartData = useMemo(() => downsample(data, maxPoints), [data, maxPoints]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const metrics = useMetrics(chartData, w, height, padL, padR);

  const { min, max, range, stepX, padding } = metrics;

  const yForIndex = useCallback(
    (i: number) => height - padding - ((chartData[i] - min) / range) * (height - padding * 2),
    [chartData, height, min, range, padding],
  );
  const xForIndex = useCallback(
    (i: number) => padL + i * stepX,
    [padL, stepX],
  );

  const updateIndex = useCallback(
    (x: number) => {
      let idx = 0;
      if (stepX > 0 && padL != null) {
        idx = Math.round((x - padL) / stepX);
      }
      idx = Math.max(0, Math.min(chartData.length - 1, idx));
      setActiveIndex(idx);
    },
    [stepX, padL, chartData.length],
  );

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: (_, gestureState) =>
          Math.abs(gestureState.dx) > Math.abs(gestureState.dy),
        onPanResponderGrant: (evt) => updateIndex(evt.nativeEvent.locationX),
        onPanResponderMove: (evt) => updateIndex(evt.nativeEvent.locationX),
        onPanResponderRelease: () => setActiveIndex(null),
        onPanResponderTerminate: () => setActiveIndex(null),
      }),
    [updateIndex],
  );

  if (chartData.length < 2 || w < 12)
    return <View style={{ height: height + X_AXIS_STRIP }} onLayout={width == null ? handleLayout : undefined} />;

  const line = smoothPath(chartData, w, height, padL, padR, padding);
  const lastX = padL + (w - padL - padR);
  const firstX = padL;   
  const fillD = `${line} L ${lastX} ${height} L ${firstX} ${height} Z`;

  const activePoint = activeIndex != null ? { x: xForIndex(activeIndex), y: yForIndex(activeIndex) } : null;
  const activeValue = activeIndex != null ? chartData[activeIndex] : null;
  const tooltipText = activeValue != null ? `${formatValue(activeValue)}${unit ? ` ${unit}` : ''}` : '';
  const tooltipWidth = tooltipText.length * 8 + 16;
  let tooltipX = activePoint ? activePoint.x - tooltipWidth / 2 : 0;
  tooltipX = Math.max(4, Math.min(w - tooltipWidth - 4, tooltipX));

  return (
    <View style={{ width: w || undefined, height: height + X_AXIS_STRIP }} onLayout={width == null ? handleLayout : undefined} {...panResponder.panHandlers}>
      <Svg width={w || 0} height={height + X_AXIS_STRIP}>
        {showYAxis && (
          <G>
            {/* y-axis spine */}
            <Line x1={padL - 5} y1={padding - 2} x2={padL - 5} y2={height - padding + 2} stroke={theme.textMuted} strokeWidth={1} opacity={0.6} />
            {(range > 1e-9 ? [min, min + range / 2, max] : [(min + max) / 2]).map((v: number, i: number) => {
              const y = height - padding - ((v - min) / range) * (height - padding * 2);
              return (
                <G key={i}>
                  <Line x1={padL - 5} y1={y} x2={w - padR} y2={y} stroke={theme.textMuted} strokeWidth={1} strokeDasharray="2 4" opacity={0.45} />
                  <SvgText x={padL - 9} y={y + 3.5} textAnchor="end" fill={theme.textMuted} fontSize={9}>
                    {formatValue(v)}
                  </SvgText>
                </G>
              );
            })}
          </G>
        )}
        <Path d={fillD} fill={fillColor} />
        <Path d={line} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
        <Circle cx={firstX} cy={yForIndex(0)} r={3} fill={color} />
        <Circle cx={lastX} cy={yForIndex(chartData.length - 1)} r={3} fill={color} />
        {showXAxis && xLabel && w > 0 && (
          <G>
            {/* x-axis spine along the plot bottom */}
            <Line x1={padL} y1={height} x2={w - padR} y2={height} stroke={theme.textMuted} strokeWidth={1} opacity={0.6} />
            {[0, 0.25, 0.5, 0.75, 1].map((f: number, i: number) => {
              const x = padL + f * (w - padL - padR);
              return (
                <G key={i}>
                  <Line x1={x} y1={height} x2={x} y2={height + 3} stroke={theme.textMuted} strokeWidth={1} opacity={0.6} />
                  <SvgText
                    x={x}
                    y={height + 13}
                    textAnchor={f === 0 ? 'start' : f === 1 ? 'end' : 'middle'}
                    fill={theme.textMuted}
                    fontSize={9}
                  >
                    {xLabel(f)}
                  </SvgText>
                </G>
              );
            })}
          </G>
        )}

        {activePoint && (
          <G>
            <Line
              x1={activePoint.x}
              y1={padding}
              x2={activePoint.x}
              y2={height - padding}
              stroke={theme.textMuted}
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            <Circle cx={activePoint.x} cy={activePoint.y} r={5} fill={color} stroke={theme.backgroundPrimary} strokeWidth={2} />
            <G transform={`translate(${tooltipX}, ${padding - 2})`}>
              <Rect x={0} y={0} width={tooltipWidth} height={22} rx={6} fill={theme.backgroundSecondaryVarient} />
              <SvgText x={tooltipWidth / 2} y={15} textAnchor="middle" fill={theme.textPrimary} fontSize={11} fontWeight="600">
                {tooltipText}
              </SvgText>
            </G>
          </G>
        )}
      </Svg>
    </View>
  );
}

interface DonutSegment {
  label: string;
  durationMs: number;
  color: string;
}

function activityBreakdown(points: TrailPoint[]): DonutSegment[] {
  const totals: Record<string, number> = {};
  for (let i = 0; i < points.length - 1; i++) {
    const dt = points[i + 1].recordedAt - points[i].recordedAt;
    if (!Number.isFinite(dt) || dt < 0 || dt > 60_000) continue; // ignore huge gaps
    const type = points[i].activityType || 'unknown';
    totals[type] = (totals[type] || 0) + dt;
  }
  return Object.entries(totals)
    .map(([label, durationMs]) => ({
      label,
      durationMs,
      color: ACTIVITY_COLORS[label] || ACTIVITY_COLORS.unknown,
    }))
    .filter((s) => s.durationMs > 0)
    .sort((a, b) => b.durationMs - a.durationMs);
}

function DonutChart({ segments, width = 140, height = 140, strokeWidth = 22 }: { segments: DonutSegment[]; width?: number; height?: number; strokeWidth?: number }) {
  if (segments.length === 0) return null;
  const radius = (Math.min(width, height) - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((sum, s) => sum + s.durationMs, 0);
  let cumulative = 0;
  const cx = width / 2;
  const cy = height / 2;

  return (
    <View style={{ width, height, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={width} height={height}>
        {segments.map((seg, i) => {
          const frac = seg.durationMs / total;
          const dash = frac * circumference;
          const startAngle = (cumulative / total) * 360 - 90;
          cumulative += seg.durationMs;
          return (
            <G key={i} transform={`rotate(${startAngle}, ${cx}, ${cy})`}>
              <Circle
                cx={cx}
                cy={cy}
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeLinecap="butt"
              />
            </G>
          );
        })}
      </Svg>
    </View>
  );
}

function formatMinutes(ms: number): string {
  return `${Math.max(1, Math.round(ms / 60000))}min`;
}

function totalElevationGain(points: TrailPoint[]): number {
  let gain = 0;
  let prev: number | undefined;
  for (const p of points) {
    if (p.altitude == null || p.altitude < 0) continue;
    if (prev != null && p.altitude > prev) gain += p.altitude - prev;
    prev = p.altitude;
  }
  return gain;
}

// Pace (min/km) per point — inverted speed; rest points would spike to infinity
function paceSeries(points: TrailPoint[]): number[] {
  return points
    .filter((p) => (p.speed ?? -1) > 0.2)
    .map((p) => 60 / ((p.speed as number) * 3.6)); // m/s → min/km
}

function paceSummary(points: TrailPoint[]): { avgPace: string; movingPace: string } | null {
  let totalTime = 0;
  let movingTime = 0;
  let distM = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const dt = (b.recordedAt ?? 0) - (a.recordedAt ?? 0);
    if (dt <= 0) continue;
    totalTime += dt;
    if ((b.speed ?? 0) > 0.3) movingTime += dt; // m/s > 0.3 ≈ moving
    // calculateDistance returns KILOMETRES (R = 6371 km) — convert to meters,
    // same as calculateTotalDistance does. Missing this made pace 1000× too big.
    distM += calculateDistance({ latitude: a.latitude, longitude: a.longitude }, { latitude: b.latitude, longitude: b.longitude }) * 1000;
  }
  if (distM < 1 || totalTime <= 0) return null;
  return {
    avgPace: formatPace(totalTime / 1000, distM),
    movingPace: movingTime > 0 ? formatPace(movingTime / 1000, distM) : '—',
  };
}

// Moving vs sniff time. Prefers the trail's own sniffTime/duration (the
// recorder's authoritative pause detection), falls back to point speeds
// (0.3 m/s threshold) when the trail record doesn't carry them.
function movingSniffBreakdown(
  points: TrailPoint[],
  sniffSeconds?: number,
  durationSeconds?: number,
): { movingMs: number; sniffMs: number } | null {
  const sniffMs = sniffSeconds != null && sniffSeconds > 0 ? sniffSeconds * 1000 : null;
  const totalMs = durationSeconds != null && durationSeconds > 0 ? durationSeconds * 1000 : null;
  if (sniffMs != null && totalMs != null && sniffMs < totalMs) {
    return { movingMs: totalMs - sniffMs, sniffMs };
  }
  let movingMs = 0;
  let pointTotalMs = 0;
  for (let i = 1; i < points.length; i++) {
    const dt = (points[i].recordedAt ?? 0) - (points[i - 1].recordedAt ?? 0);
    if (!Number.isFinite(dt) || dt <= 0 || dt > 60_000) continue;
    pointTotalMs += dt;
    if ((points[i].speed ?? 0) > 0.3) movingMs += dt;
  }
  if (pointTotalMs <= 0) return null;
  return { movingMs, sniffMs: pointTotalMs - movingMs };
}

function LegendRow({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <View style={styles.legendItem}>
      <View style={[styles.dot, { backgroundColor: color }]} />
      <View style={{ flex: 1 }}>
        <Text style={styles.legendLabel}>{label}</Text>
        <Text style={styles.legendValue}>{value}</Text>
      </View>
    </View>
  );
}

interface TrailChartsProps {
  points?: TrailPoint[];
  /** Trail-record sniff seconds (preferred source for the breakdown) */
  sniffSeconds?: number;
  /** Trail-record total duration seconds */
  durationSeconds?: number;
}

export function TrailCharts({ points, sniffSeconds, durationSeconds }: TrailChartsProps) {
  const { t } = useTranslation();
  if (!points || points.length < 3) return null;

  const altitudes = points.map((p) => p.altitude).filter((a): a is number => a != null && a >= 0);
  const elevationGain = totalElevationGain(points);
  const paceData = paceSeries(points);
  const paceStats = paceSummary(points);
  const breakdown = movingSniffBreakdown(points, sniffSeconds, durationSeconds);
  const totalKm = calculateTotalDistance(points) / 1000;

  const hasAnyChart = !!breakdown || altitudes.length >= 2 || paceData.length >= 2;
  if (!hasAnyChart) return null;

  const breakdownPct = (ms: number) =>
    breakdown && breakdown.movingMs + breakdown.sniffMs > 0
      ? Math.round((ms / (breakdown.movingMs + breakdown.sniffMs)) * 100)
      : 0;

  return (
    <View style={styles.container}>
      {breakdown && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t('walkSummary.walkBreakdown')}</Text>
          <View style={styles.donutRow}>
            <DonutChart
              segments={[
                { label: 'moving', durationMs: breakdown.movingMs, color: theme.accentSecondary },
                { label: 'sniff', durationMs: breakdown.sniffMs, color: theme.textMuted },
              ]}
              width={110}
              height={110}
              strokeWidth={20}
            />
            <View style={styles.legend}>
              <LegendRow
                label={t('walkSummary.movingTime').toUpperCase()}
                value={`${formatMinutes(breakdown.movingMs)} (${breakdownPct(breakdown.movingMs)}%)`}
                color={theme.accentSecondary}
              />
              <LegendRow
                label={t('walkSummary.sniffTime').toUpperCase()}
                value={`${formatMinutes(breakdown.sniffMs)} (${breakdownPct(breakdown.sniffMs)}%)`}
                color={theme.textMuted}
              />
            </View>
          </View>
        </View>
      )}

      {altitudes.length >= 2 && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t('walkSummary.elevation')}</Text>
          <Sparkline data={altitudes} color={theme.accentSecondary} fillColor="rgba(212,255,94,0.15)" unit="m" formatValue={(v) => `${Math.round(v)}`} showYAxis showXAxis xLabel={(f) => (f === 1 ? `${totalKm.toFixed(1)} km` : totalKm === 0 ? '0' : (totalKm * f).toFixed(1))} />
          <View style={styles.summaryRow}>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>{t('walkSummary.elevationGain').toUpperCase()}</Text>
              <Text style={styles.summaryValue}>{`+${Math.round(elevationGain)} m`}</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>{t('walkSummary.maxElevation').toUpperCase()}</Text>
              <Text style={styles.summaryValue}>{`${Math.round(Math.max(...altitudes))} m`}</Text>
            </View>
          </View>
        </View>
      )}

      {paceData.length >= 2 && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t('walkSummary.pace')}</Text>
          <Sparkline data={paceData} color={theme.accentPrimary} fillColor="rgba(255,254,119,0.12)" unit="min/km" formatValue={(v) => v.toFixed(1)} showYAxis showXAxis xLabel={(f) => (f === 1 ? `${totalKm.toFixed(1)} km` : totalKm === 0 ? '0' : (totalKm * f).toFixed(1))} />
          {paceStats && (
            <View style={styles.summaryRow}>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>{t('walkSummary.averagePace').toUpperCase()}</Text>
                <Text style={styles.summaryValue}>{`${paceStats.avgPace} min/km`}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>{t('walkSummary.movingPace').toUpperCase()}</Text>
                <Text style={styles.summaryValue}>{`${paceStats.movingPace} min/km`}</Text>
              </View>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { marginTop: 16, marginBottom: 12 },
  card: { marginBottom: 20 },
  cardTitle: { ...Typography.h3(theme.textPrimary), fontSize: 16, fontWeight: '600', marginBottom: 12 },
  donutRow: { flexDirection: 'row', alignItems: 'center', gap: 20 },
  legend: { flex: 1, gap: 14 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  dot: { width: 8, height: 8, borderRadius: 4 },
  legendLabel: { ...Typography.caption(theme.textMuted) },
  legendValue: { ...Typography.label(theme.textPrimary), marginTop: 2 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 14 },
  summaryItem: { gap: 2 },
  summaryLabel: { ...Typography.caption(theme.textMuted) },
  summaryValue: { ...Typography.label(theme.textPrimary), fontWeight: '600' },
});
